import {
  type CreateDeviceInput,
  type UpdateDeviceInput,
} from '@sitewise/shared';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { listBuildings, listUnits } from '../../../api/buildings.js';
import {
  createDevice,
  listDeviceTypes,
  updateDevice,
  type DeviceWithDeviceType,
} from '../../../api/compliance.js';
import { ApiError } from '../../../lib/api-client.js';

interface Props {
  /** Optional pre-fill, used by the building-detail page's tab. */
  buildingId?: string;
  /** When set, the sheet edits this device instead of creating. */
  device?: DeviceWithDeviceType;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  buildingId: string;
  unitId: string;
  deviceTypeId: string;
  label: string;
  make: string;
  model: string;
  serialNumber: string;
  installedAt: string;
  notes: string;
}

function toForm(device?: DeviceWithDeviceType, buildingId?: string): FormState {
  if (device) {
    return {
      buildingId: device.buildingId,
      unitId: device.unitId ?? '',
      deviceTypeId: device.deviceTypeId,
      label: device.label,
      make: device.make ?? '',
      model: device.model ?? '',
      serialNumber: device.serialNumber ?? '',
      installedAt: device.installedAt ?? '',
      notes: device.notes ?? '',
    };
  }
  return {
    buildingId: buildingId ?? '',
    unitId: '',
    deviceTypeId: '',
    label: '',
    make: '',
    model: '',
    serialNumber: '',
    installedAt: '',
    notes: '',
  };
}

export function CreateDeviceSheet({
  buildingId: initialBuildingId,
  device,
  onClose,
  onSaved,
}: Props) {
  const editing = Boolean(device);
  const buildingId = useId();
  const unitId = useId();
  const deviceTypeId = useId();
  const labelId = useId();
  const makeId = useId();
  const modelId = useId();
  const serialId = useId();
  const installedId = useId();
  const notesId = useId();

  const [form, setForm] = useState<FormState>(() => toForm(device, initialBuildingId));
  const [error, setError] = useState<string | null>(null);

  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'all'],
    queryFn: () => listBuildings({ limit: 100 }),
    enabled: !editing && !initialBuildingId,
  });
  const unitsQuery = useQuery({
    queryKey: ['building', form.buildingId, 'units'],
    queryFn: () => listUnits(form.buildingId),
    enabled: Boolean(form.buildingId),
  });
  const typesQuery = useQuery({
    queryKey: ['device-types', { active: true }],
    queryFn: () => listDeviceTypes(),
  });

  const selectedType = useMemo(() => {
    if (editing && device) return device.deviceType;
    return typesQuery.data?.deviceTypes.find((t) => t.id === form.deviceTypeId) ?? null;
  }, [editing, device, typesQuery.data, form.deviceTypeId]);

  const submit = useMutation({
    mutationFn: async () => {
      if (editing && device) {
        const input: UpdateDeviceInput = {
          unitId: form.unitId || null,
          label: form.label.trim(),
          make: form.make.trim() || null,
          model: form.model.trim() || null,
          serialNumber: form.serialNumber.trim() || null,
          installedAt: form.installedAt || null,
          notes: form.notes.trim() || null,
        };
        return updateDevice(device.id, input);
      }

      const input: CreateDeviceInput = {
        buildingId: form.buildingId,
        unitId: form.unitId || null,
        deviceTypeId: form.deviceTypeId,
        label: form.label.trim(),
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        serialNumber: form.serialNumber.trim() || null,
        installedAt: form.installedAt || null,
        warrantyExpiresOn: null,
        purchasePrice: null,
        purchaseVendor: null,
        cadenceOverride: null,
        customIntervalDaysOverride: null,
        gracePeriodDaysOverride: null,
        isComplianceTrackedOverride: null,
        notes: form.notes.trim() || null,
        metadata: {},
      };
      return createDevice(input);
    },
    onSuccess: () => {
      toast.success(editing ? 'Device updated.' : 'Device added.');
      onSaved();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.message
          : editing
            ? 'Could not update device'
            : 'Could not create device',
      );
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submit.mutate();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>{editing ? 'Edit device' : 'Add device'}</SheetTitle>
          <SheetDescription>
            {editing
              ? 'Update device details. Building and type cannot be changed.'
              : 'Compliance-tracked types schedule inspections automatically.'}
          </SheetDescription>
        </SheetHeader>

        <form className="flex flex-1 flex-col gap-3 overflow-y-auto" onSubmit={handleSubmit}>
          {!editing && !initialBuildingId ? (
            <div className="grid gap-1.5">
              <Label htmlFor={buildingId}>Building</Label>
              <select
                id={buildingId}
                required
                className="rounded border border-border bg-surface px-3 py-2 text-sm"
                value={form.buildingId}
                onChange={(e) => setForm({ ...form, buildingId: e.target.value, unitId: '' })}
              >
                <option value="">Select…</option>
                {buildingsQuery.data?.buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <Label htmlFor={deviceTypeId}>Type</Label>
            {editing ? (
              <Input
                id={deviceTypeId}
                value={device?.deviceType.name ?? ''}
                disabled
                readOnly
              />
            ) : (
              <select
                id={deviceTypeId}
                required
                className="rounded border border-border bg-surface px-3 py-2 text-sm"
                value={form.deviceTypeId}
                onChange={(e) => setForm({ ...form, deviceTypeId: e.target.value })}
              >
                <option value="">Select…</option>
                {typesQuery.data?.deviceTypes
                  .filter((t) => t.isActive)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.isComplianceTracked ? '' : ' (inventory only)'}
                    </option>
                  ))}
              </select>
            )}
          </div>

          {selectedType?.requiresUnit || (editing && form.unitId) ? (
            <div className="grid gap-1.5">
              <Label htmlFor={unitId}>Unit</Label>
              <select
                id={unitId}
                required={Boolean(selectedType?.requiresUnit)}
                className="rounded border border-border bg-surface px-3 py-2 text-sm"
                value={form.unitId}
                onChange={(e) => setForm({ ...form, unitId: e.target.value })}
                disabled={!form.buildingId}
              >
                <option value="">{selectedType?.requiresUnit ? 'Select…' : 'None'}</option>
                {unitsQuery.data?.units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.unitNumber}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <Label htmlFor={labelId}>Label</Label>
            <Input
              id={labelId}
              required
              maxLength={200}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. North elevator"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor={makeId}>Make</Label>
              <Input
                id={makeId}
                value={form.make}
                onChange={(e) => setForm({ ...form, make: e.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={modelId}>Model</Label>
              <Input
                id={modelId}
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor={serialId}>Serial #</Label>
              <Input
                id={serialId}
                value={form.serialNumber}
                onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={installedId}>Installed</Label>
              <Input
                id={installedId}
                type="date"
                value={form.installedAt}
                onChange={(e) => setForm({ ...form, installedAt: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor={notesId}>Notes</Label>
            <textarea
              id={notesId}
              rows={3}
              maxLength={8_000}
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          {error ? (
            <div className="rounded border border-danger-subtle bg-danger-subtle/40 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submit.isPending ||
                !form.buildingId ||
                !form.deviceTypeId ||
                !form.label.trim() ||
                (selectedType?.requiresUnit ? !form.unitId : false)
              }
            >
              {submit.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create device'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
